import { useEffect, useState } from 'react';

import { useManualQuery } from '@zenbusiness/application-commons-graphql';
import Grid from '@zenbusiness/zui/Grid';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import { CARD_TYPE } from '#client/constants/cards';
import { TOKEN_TYPES } from '#client/constants/tokens';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { GET_CARD_INFO } from '#client/queries';

export default function CardUsed({ cardId }) {
  const { getCustomerToken, loading: customerTokenLoading } = useCustomerToken({
    type: TOKEN_TYPES.COMMON
  });
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { unitAccountId } = useUnitAccountId();
  const [cardInfo, setCardInfo] = useState(null);

  const { createCommonErrorToast } = useToastContext();

  const [getCardInfo, { loading: getCardInfoLoading }] = useManualQuery(GET_CARD_INFO, {
    noThrow: true
  });

  useEffect(() => {
    const getCardInfoResponse = async () => {
      const customerToken = await getCustomerToken();

      if (!customerToken) {
        return;
      }

      const { data, error } = await getCardInfo({
        variables: {
          params: {
            identifier: {
              businessUuid: businessEntityUuid,
              unitAccountId
            },
            id: cardId,
            customerToken
          }
        },
        skipCache: true
      });

      if (error || !data) {
        createCommonErrorToast({ error });
        return;
      }

      setCardInfo(data.card);
    };

    if (cardId) {
      getCardInfoResponse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessEntityUuid, cardId, unitAccountId]);

  const loading = customerTokenLoading || getCardInfoLoading;

  if (!cardId || (!cardInfo && !loading)) {
    return null;
  }

  const last4DigitsText = `(••${cardInfo?.last4Digits})`;

  const cardUsedText =
    cardInfo?.type === CARD_TYPE.BUSINESS_DEBIT_CARD
      ? `Physical Card ${last4DigitsText}`
      : `Virtual Card ${last4DigitsText}`;

  return (
    <Grid
      item
      md={4}
      xs={12}
    >
      <Stack
        direction="column"
        gap="8px"
      >
        <Text
          color="tertiary"
          size="xs"
          uppercase
        >
          Card Used
        </Text>
        {loading ? (
          <Skeleton width="150px" />
        ) : (
          <Text
            color="primary"
            size="lg"
          >
            {cardUsedText}
          </Text>
        )}
      </Stack>
    </Grid>
  );
}

CardUsed.propTypes = {
  cardId: PropTypes.string
};
